import { Component, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, FormControl } from '@angular/forms';
import { Router, ActivatedRoute }                   from '@angular/router';

import { FieldManualService }       from './../../../../field-manual.service';
import { ProgramService }           from './../../../../../../shared/services/program.service';
import { CsvService }               from './../../../../csv.service';
import { TimezoneService }          from './../../../../../../shared/services/timezones.service';
import { GroupService }             from './../../../../group.service';
import { ErrorService }             from './../../../../../../shared/services/error.service';
import { ProgramRoutesService }     from './../../../program-routes.service';
import { SearchService }            from '../../../../../../shared/services/search.service';

import { Admin }                    from './../../../../../../models/program/admin';
import { GroupSingle }              from './../../../../../../models/program/group-single';
import { GroupWaveSharedSingle }    from './../../../../../../models/program/group-wave-shared-single';
import { WaveSingle }               from '../../../../../../models/program/wave-single';
import { ProgramSingle }            from './../../../../../../models/program/program-single';
import { NewUser }                  from '../../../../../../models/program/new-user';
import { NewAndExistingUsers }      from '../../../../../../models/program/new-and-existing-users';
import { BatchCommitInfo }          from '../../../../../../models/program/batch-commit-info';

import { ConnectableObservable }    from 'rxjs/observable/ConnectableObservable';
import { Observable }               from 'rxjs/Observable';
import { Subscription }             from 'rxjs/Subscription';
import { FmRelease } from '../../../../../../models/program/fm-release';
import { EditionListService } from '../../../../../../editions/edition.service';

@Component({
  selector: 'app-group-settings',
  templateUrl: './group-settings.component.html',
  styleUrls: ['./group-settings.component.less']
})

export class GroupSettingsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() programId:     number;
  // Either defined through custom component (cc) input binding, or assigned in OnInit
  @Input() componentContext:  'existing-team-routed' | 'existing-wave-routed' | 'new-batch-cc' | 'new-team-from-existing-users-cc';
  // userList for creation of new team from existing users
  @Input() newAndExistingUserList?: NewAndExistingUsers;
  // batchCommitInfo for creation from spreadsheet
  @Input() batchCommitInfo:     BatchCommitInfo;
  // inputType - for newly created teams or waves
  @Input() inputType:     'group' | 'wave' | 'lone_group';
  @Output() reset:        EventEmitter<boolean> = new EventEmitter<boolean>();

  // GroupId for retrieving existing settings. If creating a new group, the group doesn't exist, so no groupId will exist.
  public groupId:         number;
  public retrievedGroupWaveSettingsValues:   GroupSingle | WaveSingle;
  public settings:        FormGroup;
  public program_show_all_time_leaders:  boolean;
  public fms:             { id: number; title: string; }[] = [];
  public analyticsAdmins: Admin[] = [];
  public errors:          string[];
  public name:            string;
  public owner:           string;
  public errMessage:      string;
  public committed:       boolean;
  public saving:          boolean;
  public saved:           boolean;
  private subs:           Subscription[] = [];
  public inputTypeForDisplay: 'wave' | 'team';

  public tabIndex = 0;
  readonly tabOptions = ['general-settings', 'schedule', 'notifications'];
  public activatedValidationIndex: number;
  private fullListObs: ConnectableObservable<Admin[]>;

  constructor(
    private _fb: FormBuilder,
    private fieldManualService: FieldManualService,
    private programService: ProgramService,
    private csvService: CsvService,
    private groupService: GroupService,
    private router: Router,
    public tz: TimezoneService,
    private errorService: ErrorService,
    private route: ActivatedRoute,
    private _programRoutes: ProgramRoutesService,
    private editionListService: EditionListService,
    private search: SearchService
  ) { }

  public ngOnInit(): void {
    // Retrieve full admin list for autocomplete
    this.fullListObs = this.search.queryAdmins('')
      .map((fullList) => fullList
        .map((admin => {return {name: admin.name, id: admin.id} as Admin}))
      ).publishReplay()
    this.subs.push(this.fullListObs.connect());

    // When displayed to user, only 'team' terminology should be used, not 'group'
    if (this.inputType === 'group' || this.inputType === 'lone_group') {
      this.inputTypeForDisplay =  'team'
    } else {
      this.inputTypeForDisplay = this.inputType;
    }
    if (!this.componentContext && this.route.parent.parent) {
      // This component can be routed to, or can be used as a custom component. This should test for the former case.
      this.route.parent.url.subscribe(urlArray => {
        if (urlArray[0].path === 'waves') {
          this.componentContext = 'existing-wave-routed';
        } else if (urlArray[0].path === 'teams') {
          this.componentContext = 'existing-team-routed';
        }
      });

      this.programId = this.groupService.processRouteForParentId(this.route.snapshot);

      this.subs.push(this.route.parent.params.subscribe(params => {
        this.groupId = +params['groupId'];
      }));

      // Retrieve settings values for existing groups
      if (this.componentContext === 'existing-wave-routed') {
        this.groupService.getWave(this.programId, this.groupId)
          .subscribe(wave => {
            this.inputType = 'wave';
            this.inputTypeForDisplay = 'wave';
            this.retrievedGroupWaveSettingsValues = wave;
            this._programRoutes.name.next(wave.name);
            this.initializeComponent();
          });
      } else if (this.componentContext === 'existing-team-routed') {
        this.groupService.getGroup(this.programId, this.groupId)
            .subscribe(group => {
              this.inputType = (group.wave ? 'group' : 'lone_group');
              this.inputTypeForDisplay = 'team';
              this.retrievedGroupWaveSettingsValues = group;
              this._programRoutes.name.next(group.name);
              this.initializeComponent();
            });
      }

      this.route.data.subscribe(result => {
        this.tabIndex = this.tabOptions.indexOf(result.settings);
        if (this.componentContext !== 'existing-wave-routed') {
          this.activatedValidationIndex = this.tabIndex;
        }
      });

    } else {
      this.initializeComponent();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['retrievedGroupWaveSettingsValues'] && this.settings) {
      this.settings.patchValue(this.retrievedGroupWaveSettingsValues);
    }
  }

  public ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
  }

/**
 * Public Methods
 */

  public noRelease(index: number): void {
    this.setFmReleaseDate(null, index);
  }

  public releaseNow(index: number): void {
    let fullDateString = this.createNowReleaseString();
    this.setFmReleaseDate(fullDateString, index);
  }

  public createNowReleaseString(): string {
    let now: Date = new Date(Date.now());
    let month: string = this.formatDateTime(now.getMonth() + 1);
    let day: string = this.formatDateTime(now.getDate());
    let hour: string = this.formatDateTime(now.getHours());
    let minutes = '00';
    let seconds = '00';
    let dateString: any = `${now.getFullYear()}-${month}-${day}`;
    let timeString: any = `${hour}:${minutes}`;
    return `${dateString}T${timeString}:${seconds}Z`;
  }

  private setFmReleaseDate(dateTimeString: string, index: number) {
    let fmArray: FormArray = <FormArray>this.settings.controls['fm_releases'];
    let fmGroup: FormGroup = <FormGroup>fmArray.at(index)
    fmGroup.markAsDirty();
    fmGroup.controls['release'].markAsDirty();
    fmGroup.controls['release'].setValue(dateTimeString);
  }

  public notificationToggle(field: string, value: boolean): void {
    const control: FormControl = <FormControl>this.settings.controls[field];
    control.patchValue(value);
    console.log(control.value);
  }

  public save(settingsValues: any, isValid: boolean): void {
    let model = Object.assign({}, settingsValues);
    console.log(model, isValid);
    this.saving = true;
    this.saved = false;
    if (this.componentContext === 'new-batch-cc' || this.componentContext === 'new-team-from-existing-users-cc') {
      this.commit(this.programId, model);
    } else if (this.componentContext === 'existing-team-routed') {
      this.editGroup(this.programId, this.retrievedGroupWaveSettingsValues.id, model);
    } else if (this.componentContext === 'existing-wave-routed') {
      this.editWave(this.programId, this.retrievedGroupWaveSettingsValues.id, model)
    }
  }

  public cancel(): void {
    this.reset.emit(true);
  }

  public next(): void {
    this.activatedValidationIndex  = this.tabIndex;
    if (this.checkTabValidation(this.tabIndex)) {
      this.changeTab(1);
    }
  }

  public previous(): void {
    this.changeTab(-1);
  }

  public changeTab(shift: number) {
    this.tabIndex = this.tabIndex + shift;
    // event.preventDefault();
  }

  public resetField(field: string): void {
    this.settings.get(field).setValue(this.retrievedGroupWaveSettingsValues[field]);
  }

  public resetReleaseField(index: number): void {
    let formArray = <FormArray>this.settings.get('fm_releases')
    formArray.controls[index].setValue(this.retrievedGroupWaveSettingsValues.fm_releases[index]);
  }

/**
 * Private Methods
 */
  private isGroup(value: WaveSingle | GroupSingle): value is GroupSingle {
    return value.hasOwnProperty('wave');
  }

  private checkTabValidation(tabIndex: number): boolean {
    switch (this.tabOptions[tabIndex]) {
      case 'general-settings':
        return this.checkGeneralSettings();
      case 'schedule':
        return this.checkSchedule();
      case 'notifications':
        return this.checkNotifications();
    }
  }

  private checkGeneralSettings(): boolean {
    if (this.componentContext === 'new-batch-cc') {
      return true;
    } else {
      return !!this.settings.controls['name'].value;
    }
  }

  private checkSchedule(): boolean {
    if (
      this.componentContext === 'existing-wave-routed' ||
      ( this.settings.controls['timezone'].value &&
        this.settings.controls['start_date'].value &&
        this.checkFms(this.settings.controls['fm_releases'].value)
      )
    ) {
      return true;
    }
    return false;
  }

  private checkNotifications(): boolean {
    return true;
  }

  private checkFms(fms: any[]): boolean {
    let fmSet = false;
    fms.forEach(fm => {
      if (fm.release) {
        fmSet = true;
        return;
      };
    });
    if (fmSet === false) {
      // this.tabUnfinished.push('Please set a release date for at least one Field Manual.');
    }
    return fmSet;
  }

  private fmArrayValidation(formArray: FormArray) {
    if (this.componentContext === 'existing-wave-routed') {
      return null;
    } else {
      return formArray.controls.reduce((acc, currentValue: FormControl): any => {
        return currentValue.get('release').value ? null : acc;
      }, {'no_fm_release': 'Please provide a release date for at least one field manual'})
    }
}

  public buildForm(batchCommitInfo?: BatchCommitInfo): FormGroup {
    let group: {};
    let formBasics = {
      name: ['', <any>Validators.required],
      analytics_admins: this._fb.control([]),
      timezone: ['', <any>Validators.required],
      start_date: ['', <any>Validators.required],
      end_date: [null],
      fm_releases: this._fb.array([], this.fmArrayValidation.bind(this)),
      fm_release_notification: [false],
      weekly_individual_leaders: [false],
      weekly_top_teams: [false],
      one_week_remaining: [false],
      one_day_remaining: [false],
      notify_users_after_inactivity: [false],
      nudge_delay: [null],
      notify_launch: [false],
      notify_followup: [false],
      notify_followup_delay: [null],
      include_in_all_time_leaders: [true],
      include_in_team_leaders: [true],
      hide_leaderboards: [false],
    }
    switch (this.componentContext) {
      case 'new-batch-cc':
        group = {
          ...formBasics,
          commit_info: batchCommitInfo
        };
        break;
      case 'existing-team-routed':
        group = {
          ...formBasics,
          wave_id: ['']
        };
        break;
      case 'new-team-from-existing-users-cc':
        group = {
          ...formBasics,
          wave_id: ['']
        };
        break;
      case 'existing-wave-routed':
        group = {
          ...formBasics,
          program_admins: this._fb.array([]),
          _differs: [],
          _release_differs: []
        };
    }
    return this._fb.group(group);
  }

  private formatDateTime(int: number): string {
    let measure: string;
    if (int < 10) {
      measure = `0${int}`;
    } else {
      measure = int.toString();
    }
    return measure;
  }

  private formSetup(): void {
    if (this.batchCommitInfo) {
      console.log(this.batchCommitInfo);
      this.settings = this.buildForm(this.batchCommitInfo);
    } else {
      console.log('no batch');
      this.settings = this.buildForm();
    }
  }

  private groupSetup(): void {
    if (this.retrievedGroupWaveSettingsValues) {
      this.name = this.retrievedGroupWaveSettingsValues.name;
    } else {
      this.name = '';
    }
    if (this.retrievedGroupWaveSettingsValues && this.retrievedGroupWaveSettingsValues.start_date) {
      this.settings.controls['start_date'].setValue(this.retrievedGroupWaveSettingsValues.start_date);
    }
    if (this.retrievedGroupWaveSettingsValues && this.retrievedGroupWaveSettingsValues.end_date) {
      this.settings.controls['end_date'].setValue(this.retrievedGroupWaveSettingsValues.end_date);
    }
    if (this.retrievedGroupWaveSettingsValues && this.isGroup(this.retrievedGroupWaveSettingsValues)
        && this.retrievedGroupWaveSettingsValues.wave) {
      this.settings.controls['wave_id'].patchValue(this.retrievedGroupWaveSettingsValues.wave.id);
    } else if (this.retrievedGroupWaveSettingsValues && this.isGroup(this.retrievedGroupWaveSettingsValues)) {
      this.settings.controls['wave_id'].patchValue(null);
    }
    console.log('this.name:', this.name);
  }

  private addFmReleaseForm(releaseForm: FormGroup): void {
    const control: FormArray = <FormArray>this.settings.controls['fm_releases'];
    control.push(releaseForm);
  }

  private buildFmReleaseForm(fm: {id, release?}): FormGroup {
    return this._fb.group({
      id: [fm.id, Validators.required],
      release: [fm.release]
    });
  }

  private commit(programId: number, model: any): void {
    console.log(model);
    if (this.componentContext === 'new-team-from-existing-users-cc') {
      model['user_list'] = this.newAndExistingUserList;
      this.groupService.createFromExisting(this.programId, model)
                       .subscribe(res => {
                         this.reset.emit();
                         this.errors = null;
                         this.saving = false;
                         this.settings.markAsPristine();
                       }, error => {
                         this.saving = false;
                         this.errors = this.errorService.messages(error);
                       });
    } else if (this.componentContext === 'new-batch-cc') {
      this.csvService.commitBatchTeams(programId, model)
        .subscribe(res => {
          console.log(res);
          this.saving = false;
          this.committed = true;
          this.groupService._groups = null;
          this.reset.emit(true);
        }, error => {
          this.saving = false;
          this.errors = this.errorService.messages(error);
        });
    }
  }

  private editGroup(programId: number, groupId: number, model: Partial<GroupSingle>): void {
    let keysToBeSaved: string[];
    switch (this.tabOptions[this.tabIndex]) {
      case 'general-settings':
        keysToBeSaved = ['name', 'analytics_admins', 'include_in_all_time_leaders', 'include_in_team_leaders'];
        break;
      case 'schedule':
        keysToBeSaved = ['timezone', 'start_date', 'end_date', 'fm_releases'];
        break;
      case 'notifications':
        keysToBeSaved = ['fm_release_notification', 'weekly_individual_leaders',
          'weekly_top_teams', 'one_week_remaining', 'one_day_remaining', 'notify_users_after_inactivity',
          'nudge_delay', 'notify_launch', 'notify_followup', 'notify_followup_delay'];
        break;
    }
    let patchModel = {}
    keysToBeSaved.map(key => patchModel[key] = model[key]);

    this.groupService.editSettings(this.programId, this.retrievedGroupWaveSettingsValues.id, model)
        .subscribe(res => {
          this.saving = false;
          this.saved = true;
          this.errors = null;
          this.reset.emit(true);
          if (this.settings.controls['name'].dirty) {
            this.editionListService.refreshCurrentEdition();
          }
          this.settings.markAsPristine();
        }, error => {
          this.saving = false;
          this.errors = this.errorService.messages(error);
        });
  }

  private editWave (programId: number, waveId: number, model: Partial<GroupSingle>): void {
      // Get the form value, then remove anything that is pristine
      Object.keys(model).forEach(key => {
        let control = this.settings.controls[key];
        if (control.pristine) {
          delete model[key];
        }
      });
      if (model.fm_releases) {
        let removals = [];
        let formArray = <FormArray>this.settings.controls['fm_releases']
        model.fm_releases.forEach((release, index) => {
          let control = formArray.controls[index];
          if (control.pristine) {
            removals.push(index);
          }
        });
        removals.sort();
        while (removals.length) {
          let index = removals.pop();
          model.fm_releases.splice(index, 1);
        }
      }

      console.log(model);

      this.groupService.editWave(programId, waveId, model)
          .subscribe(res => {
            this.saving = false;
            this.saved = true;
            this.reset.emit(true);
            this.errors = null;
            if (this.settings.controls['name'].dirty) {
              this.editionListService.refreshCurrentEdition();
            }
            this.settings.markAsPristine();
            this.groupService.getWave(this.programId, this.groupId)
              .subscribe(wave => {
                this.retrievedGroupWaveSettingsValues = wave;
              });
          }, error => {
            this.saving = false;
            this.errors = this.errorService.messages(error);
          });
  }

  private initializeComponent(): void {
    this.formSetup();
    this.groupSetup();
    this.programService.getProgram(this.programId)
      .subscribe(program => this.program_show_all_time_leaders = program.show_all_time_leaders);

    if (this.retrievedGroupWaveSettingsValues) {
      this.retrievedGroupWaveSettingsValues.fm_releases.forEach(fmDescDetails => {
        this.addFmReleaseForm(this.buildFmReleaseForm(fmDescDetails));
        this.fms.push(fmDescDetails);
      });
      this.settings.patchValue(this.retrievedGroupWaveSettingsValues);
    } else {
      // Get list of FM releases from fieldManualService
      this.fieldManualService
        .getFieldManuals(this.programId)
        .subscribe(fms => {
          fms.forEach(fm => {
            let fmRelease: FmRelease = {
              title: fm.title,
              id: fm.id,
            }
            if (this.componentContext === 'new-team-from-existing-users-cc' || this.componentContext === 'new-batch-cc') {
              fmRelease.release = this.createNowReleaseString();
            }
            this.addFmReleaseForm(this.buildFmReleaseForm(fmRelease));
            this.fms.push(fmRelease);
          });
        });
    }

    console.log(this.retrievedGroupWaveSettingsValues);
    console.log(this.settings.value);
  }

  public requestAutocompleteItems = (name: string): Observable<Admin[]> => {
    return this.fullListObs
  }
}



// WEBPACK FOOTER //
// /home/runner/work/nomadic-admin/nomadic-admin/nomadic-admin/src/app/dashboard/program-list/program/groups/group/group-settings/group-settings.component.ts